import { TooltipWrapper } from 'components/TooltipWrapper/TooltipWrapper';
import { Badge } from 'uikit';

import type { ICellRendererParams } from 'ag-grid-community';
import * as R from 'ramda';
import type React from 'react';
import { useMemo } from 'react';

interface ICodeCellRendererComponent<T> extends ICellRendererParams<T> {
    field: keyof T;
    dotNotationField: string | string[];
}

const badgeStyle = { marginRight: 5 };

type TCodeCellData = {
    isRevisionPrice?: boolean | null;
    isUpdatedPrice?: boolean | null;
    isActKC2Price?: boolean | null;
    isAddedRow?: boolean | null;
};

export const CodeCellRendererComponent = <T extends TCodeCellData>(props: ICodeCellRendererComponent<T>) => {
    const { field, dotNotationField, data, formatValue } = props;

    const value = useMemo(() => {
        if (data === undefined) {
            return '';
        }

        if (data[field]) {
            return formatValue?.(data[field]);
        }

        if (Array.isArray(dotNotationField)) {
            const someDotNotationFieldHasName = dotNotationField.find((dotNotationFieldItem) =>
                R.path(R.split('.', dotNotationFieldItem), data),
            );

            return (
                (someDotNotationFieldHasName &&
                    formatValue?.(R.path(R.split('.', someDotNotationFieldHasName), data))) ??
                ''
            );
        }

        return formatValue?.(R.path(R.split('.', dotNotationField), data)) ?? '';
    }, [data, dotNotationField, field, formatValue]);

    const badge = useMemo(() => {
        if (data?.isRevisionPrice || data?.isUpdatedPrice) {
            if (data?.isRevisionPrice) {
                return (
                    <TooltipWrapper message="Ревизия сметы" style={badgeStyle} key="revision" isInteractive={false}>
                        <Badge size="s" status="normal" label="Р" />
                    </TooltipWrapper>
                );
            }

            if (data?.isUpdatedPrice) {
                return (
                    <TooltipWrapper message="Смета обновлена" style={badgeStyle} key="update" isInteractive={false}>
                        <Badge size="s" label="О" style={{ background: 'rgba(255, 0, 208, 1)' }} />
                    </TooltipWrapper>
                );
            }
        } else if (data?.isActKC2Price) {
            return (
                <TooltipWrapper message="Добавлено из Акта КС-2" size="m" isInteractive={false}>
                    <Badge style={badgeStyle} size="s" status="success" label="Д" />
                </TooltipWrapper>
            );
        } else if (data?.isAddedRow) {
            return (
                <TooltipWrapper message="Добавлено из Изм. к НЗ" size="m" isInteractive={false}>
                    <Badge style={badgeStyle} size="s" status="success" label="Д" />
                </TooltipWrapper>
            );
        }

        return undefined;
    }, [data?.isActKC2Price, data?.isAddedRow, data?.isRevisionPrice, data?.isUpdatedPrice]);

    return badge ? (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            {badge}
            {value}
        </div>
    ) : (
        <span>{value}</span>
    );
};

CodeCellRendererComponent.displayName = 'CodeCellRendererComponent';
